import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FEED_STATUS, useNotifications } from '../hooks/useNotifications.js';
import { readSession } from '../_helpers/session.js';

const noop = () => {};
const resolvedFalse = () => Promise.resolve(false);

/**
 * What consumers see without a provider (tests, the OBS overlay, anything
 * rendered outside the app shell): an idle feed with nothing to show and
 * actions that do nothing. Same shape as useNotifications() so nobody has
 * to branch on the provider being there.
 */
export const DISABLED_NOTIFICATIONS = Object.freeze({
  items: [],
  unread: { count: 0, capped: false },
  status: FEED_STATUS.IDLE,
  error: null,
  nextCursor: null,
  hasMore: false,
  loadingMore: false,
  loadMoreError: null,
  mutationError: null,
  pendingIds: new Set(),
  connectionStatus: 'disconnected',
  announcement: null,
  reload: noop,
  loadMore: resolvedFalse,
  markRead: resolvedFalse,
  markAllRead: resolvedFalse,
  dismissMutationError: noop,
  hasSession: false,
});

const NotificationsContext = createContext(DISABLED_NOTIFICATIONS);

/**
 * One notification feed and live event stream for the whole app (ENG-26). Sits
 * inside the Router so it can re-check the session on every navigation:
 * sessionStorage fires no events, but every sign-in and sign-out here ends
 * in a route change (OAuthPage → /home, fetchWrapper → /login), so that is
 * the moment the feed starts or stops.
 */
export function NotificationsProvider({ children }) {
  const location = useLocation();
  const [hasSession, setHasSession] = useState(readSession);

  useEffect(() => {
    setHasSession(readSession());
  }, [location.pathname]);

  const feed = useNotifications({ enabled: hasSession });

  return <NotificationsContext.Provider value={{ ...feed, hasSession }}>{children}</NotificationsContext.Provider>;
}

export const useNotificationsContext = () => useContext(NotificationsContext);

export default NotificationsContext;
